import { useState, useEffect, memo, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { Stack, Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { icons, images, svgIcons } from "helpers/images";
import {
  RectangleGroupIcon,
  PlayIcon,
  CircleStackIcon,
  WrenchIcon,
  RectangleStackIcon,
  AcademicCapIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BookOpenIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { repoMapping } from "helpers/constant";
import { checkForSingleCounselling } from "helpers";

const Sidebar = ({
  data,
  containerTrigger,
  handleContainerTrigger,
  selectedFeature,
  setSelectedFeature,
}) => {
  const location = useLocation();
  const urlPath = location.pathname;
  const featureList = data?.map((feature) => feature.feature) || [];
  const [queuedFeature, setQueuedFeature] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const sideMenus = [
    {
      label: "Dashboard",
      path: `/portal/dashboard`,
      icon: <RectangleGroupIcon className={`h-6 w-6 aspect-square`} />,
      key: "dashboard",
    },
    {
      label: "Videos",
      path: `/videos`,
      icon: <PlayIcon className={`h-6 w-6 aspect-square pl-[2px]`} />,
      key: "videos",
    },
    {
      title: "Insights",
      icon: <CircleStackIcon className={`h-6 aspect-square`} />,
      menus: [
        {
          icon: <svgIcons.allotment className={`h-6 aspect-square`} />,
          label: "Allotments",
          key: "allotments",
        },
        {
          icon: <svgIcons.closingRank className={`h-6 aspect-square`} />,
          label: "Closing Ranks",
          key: "closingranks",
        },
        {
          icon: <svgIcons.seatMatrix className={`h-6 aspect-square`} />,
          label: "Seat Matrix",
          key: "seatmatrix",
        },
        {
          icon: <svgIcons.feeStipendBond className={`h-6 aspect-square`} />,
          label: "Fee, Stipend and Bond",
          key: "feestipendbond",
        },
      ],
    },
    {
      title: "Tools",
      icon: <WrenchIcon className={`h-6 aspect-square`} />,
      menus: [
        {
          icon: <svgIcons.allotmentMapping className={`h-6 aspect-square`} />,
          label: "Allotment Mapping",
          path: `/portal/allotmentmapping`,
          key: "allotmentmapping",
        },
        {
          icon: <MagnifyingGlassIcon className={`h-6 aspect-square`} />,
          label: "Rank Scan",
          path: `/portal/rankscan`,
          key: "rankscan",
        },
        {
          icon: <svgIcons.seatIncrease className={`h-6 aspect-square`} />,
          label: "Seat Increase",
          path: `/portal/seatincrease`,
          key: "seatincrease",
        },
      ],
    },
    {
      title: "Explore",
      icon: <RectangleStackIcon className={`h-6 aspect-square`} />,
      menus: [
        {
          icon: <BuildingOffice2Icon className={`h-6 aspect-square`} />,
          label: "Institutes",
          path: `/portal/institutes`,
          key: "institutes",
        },
        {
          icon: <BuildingLibraryIcon className={`h-6 aspect-square`} />,
          label: "Universities",
          path: `/portal/universities`,
          key: "universities",
        },
        {
          icon: <svgIcons.counselling className={`h-6 aspect-square`} />,
          label: "Counsellings",
          path: `/portal/counsellings`,
          key: "counsellings",
        },
        {
          icon: <AcademicCapIcon className={`h-6 aspect-square`} />,
          label: "Courses",
          path: `/portal/courses`,
          key: "courses",
        },
      ],
    },
    {
      icon: <DocumentTextIcon className={`h-6 aspect-square`} />,
      label: "Resources",
      path: `/portal/resources`,
      key: "resources",
    },
  ];

  const findMenuPath = (menus) => {
    for (const menu of menus) {
      if (urlPath.includes(menu.key)) {
        return { accordion: null, feature: menu.key };
      }

      if (menu.menus) {
        const result = findMenuPath(menu.menus, urlPath);

        if (result) {
          result["accordion"] = menu.title;
          return result;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    let match = findMenuPath(sideMenus);
    if (match) {
      if (match["accordion"]) {
        setExpandedAccordions((prev) => ({
          ...prev,
          [match["accordion"]]: true, // Toggle only the clicked accordion
        }));
      }
      if (match["feature"] && selectedFeature !== match["feature"])
        setSelectedFeature(match["feature"]);
    }
  }, [urlPath]);

  useEffect(() => {
    if (queuedFeature) {
      if (containerTrigger !== queuedFeature) handleContainerTrigger(queuedFeature);
    } else handleContainerTrigger(null);
  }, [queuedFeature]);

  useEffect(() => {
    if (queuedFeature && containerTrigger !== queuedFeature) setQueuedFeature(null);
  }, [containerTrigger]);

  const handleSidebarSelection = (iconName) => {
    setSelectedFeature(iconName);
    if (queuedFeature) setQueuedFeature(null);
  };
  const handleSidebarQueueing = (iconName) => {
    if (queuedFeature !== iconName) {
      setQueuedFeature(iconName);
    } else setQueuedFeature(null);
  };
  const handleToggle = (title) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [title]: !prev[title], // Toggle only the clicked accordion
    }));
  };

  const SidebarNavigationItem = memo(
    ({ path, accordionKey, index, icon, label, isAccordionElement, data }) => {
      const [redirectPath, setRedirectPath] = useState(null);
      let iconBgColor =
        selectedFeature === accordionKey
          ? "!bg-primary group-hover/item:!bg-primary-dark"
          : queuedFeature === accordionKey
          ? "bg-grey-200"
          : isAccordionElement
          ? "bg-grey50 group-hover/item:!bg-grey-200"
          : "bg-white group-hover/item:!bg-grey-200";
      let iconTextColor = selectedFeature === accordionKey ? "text-white" : "text-black";
      const featureIcon = (
        <div
          className={`min-w-10 max-w-10 min-h-10 max-h-10 w-10 h-10 aspect-square flex items-center justify-center  rounded-full cursor-pointer ${iconBgColor} ${iconTextColor}
           transition-all duration-200 ease-in-out `}
        >
          {icon}
        </div>
      );
      const featureContent = (
        <p
          className={` text-xs font-medium text-center cursor-pointer ${
            selectedFeature === accordionKey ? "!text-primary" : "!text-black"
          }
                 transition-all duration-200 ease-in-out `}
        >
          {label}
        </p>
      );

      useEffect(() => {
        let featureCounsellingList = data?.find(
          (feature) => feature.feature === repoMapping[accordionKey]
        )?.counsellings;
        if (featureCounsellingList) {
          let states = Object.keys(featureCounsellingList);
          if (featureCounsellingList && states.length === 1) {
            let redirectCounsellingId = checkForSingleCounselling(
              featureCounsellingList,
              states[0]
            );
            if (redirectCounsellingId)
              setRedirectPath(`/portal/${accordionKey}?counselling_id=${redirectCounsellingId}`);
          }
        }
      }, []);

      return path || redirectPath ? (
        <Link
          to={path || redirectPath}
          key={`${accordionKey}-${index}`}
          className="group/item flex flex-col items-center gap-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleSidebarSelection(accordionKey);
          }}
        >
          {featureIcon}
          {featureContent}
        </Link>
      ) : (
        <div
          key={`${accordionKey}-${index}`}
          className="group/item flex flex-col items-center gap-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleSidebarQueueing(accordionKey);
          }}
        >
          {featureIcon}
          {featureContent}
        </div>
      );
    }
  );

  const GroupIcons = ({ item, index, expanded, onToggle }) => {
    return (
      <div className="flex flex-col items-center gap-2" key={`sidebar-${index}`}>
        <Accordion
          expanded={expanded}
          onChange={onToggle}
          sx={{
            padding: "0px !important",
            borderRadius: "9999px !important", // Full rounded
            transition: "all 0.3s ease-in-out",
            backgroundColor: expanded ? "white" : "transparent",
            boxShadow: "none", // Removes shadow
            display: "flex",
            flexDirection: "column",
            maxWidth: "80px",
          }}
        >
          {/* Accordion Summary (Header) */}
          <AccordionSummary
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              transition: "all 0.3s ease-in-out",
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: "0px ",
              },
              "& .MuiAccordionSummary-content": {
                margin: "0px",
              },
            }}
          >
            <div className="group/item flex flex-col items-center justify-center w-full gap-1">
              <div
                className={`!min-w-10 min-h-10 aspect-square flex items-center justify-center rounded-full group-hover/item:!bg-grey-200`}
                style={{ background: "white" }}
              >
                {item?.icon}
              </div>
              <div
                className={`flex items-center justify-center gap-[6px] pb-2 ${
                  expanded ? " border-b-[0.5px] border-grey-border" : ""
                }`}
              >
                <p className="text-xs font-medium">{item.title}</p>
                <span className={`${expanded ? "rotate-180" : ""}`}>
                  <ChevronDownIcon className="w-[10px] h-[10px] stroke-[3px]" />
                </span>
              </div>
            </div>
          </AccordionSummary>

          {/* Accordion Details (Content) */}
          <AccordionDetails
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              paddingRight: "20px",
              paddingLeft: "20px",
              borderRadius: "9999px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {item?.menus.map(({ icon, label, path = null, key }, index) => {
              return (
                featureList.includes(label) && (
                  <SidebarNavigationItem
                    path={path}
                    accordionKey={key}
                    icon={icon}
                    label={label}
                    index={index}
                    isAccordionElement={true}
                    data={data}
                  />
                )
              );
            })}
          </AccordionDetails>
        </Accordion>
      </div>
    );
  };

  const SideBarIcon = ({ sideBarItem, itemIndex }) => {
    return sideBarItem?.menus?.length > 0 ? (
      featureList.some((item) => sideBarItem?.menus.some((menu) => menu.label.includes(item))) && (
        <GroupIcons
          item={sideBarItem}
          index={itemIndex}
          expanded={expandedAccordions ? expandedAccordions[sideBarItem?.title] || false : false}
          onToggle={() => handleToggle(sideBarItem?.title)}
        />
      )
    ) : (
      <SidebarNavigationItem
        path={sideBarItem?.path}
        accordionKey={sideBarItem?.key}
        icon={sideBarItem?.icon}
        label={sideBarItem?.label}
        index={itemIndex}
        isAccordionElement={false}
      />
    );
  };

  return (
    <div
      id="headerNavBar"
      className="flex flex-col items-center min-w-20 max-w-24 pb-10 h-screen overflow-auto scrollbar-hover"
    >
      <div
        id="navbar-zynerd-logo"
        className="flex w-full items-center justify-center sticky top-0 bg-grey-50 pb-3 z-10"
      >
        <div
          className="flex items-center justify-center rounded-full min-w-14 min-h-14 bg-white cursor-pointer"
          onClick={() => {
            window.open("/portal/dashboard", "_self");
          }}
        >
          <img
            src={images.zynerdIconOnly}
            className="flex justify-center items-center max-h-7 aspect-square"
            alt="logo"
          />
        </div>
      </div>
      <Stack spacing={1.5} className="flex flex-col items-center ">
        {sideMenus.map((item, index) => {
          return <SideBarIcon sideBarItem={item} itemIndex={index} />;
        })}
      </Stack>
    </div>
  );
};
export default Sidebar;
